import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { orderApi } from "../../../api/order";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { useTheme } from "../../../context/ThemeContext";
import { Package2, Truck, CreditCard, MapPin, CheckCircle2, CircleDashed, XCircle } from "lucide-react";

/* ===== Helpers ===== */
const fmt = (v: number, ccy = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: ccy }).format(v || 0);

const tryParse = (s?: string|null) => { try { return s ? JSON.parse(s) : null } catch { return null } };

/* ===== Theme surfaces ===== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    surface1: isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)",
    surface2: isDark ? "rgba(255,255,255,.06)"  : "rgba(0,0,0,.06)",
    divider:  isDark ? "rgba(255,255,255,.10)"  : "rgba(0,0,0,.12)",
    softShadow: isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)",
    textPrimary: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
    warn: "#f59e0b",
    danger: "#ef4444",
  };
};
const panel = (S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: S.surface1,
  borderColor: S.divider,
  borderWidth: 1,
  boxShadow: S.softShadow,
});

/* ===== Badges ===== */
const statusMap: Record<number, string> = {
  0: "Alındı",
  6: "Kargoda",
  7: "Teslim",
  8: "İptal",
  9: "İade",
  4: "Teslim Edilemedi",
  18:"Teslim Edilemedi",
};
const payMap: Record<number, string> = {
  0: "Ödenmedi",
  1: "Ödendi",
  2: "Kısmi",
  3: "İade",
};

const StepDot: React.FC<{active?:boolean; done?:boolean; danger?:boolean;}> = ({active, done, danger}) => {
  if (danger) return <XCircle size={18} className="text-rose-500"/>;
  if (done) return <CheckCircle2 size={18} className="text-emerald-500"/>;
  if (active) return <CircleDashed size={18} className="text-blue-500"/>;
  return <CircleDashed size={18} className="text-gray-400"/>;
};

const AddressBox: React.FC<{title:string; json?:string|null; S:any}> = ({title, json, S}) => {
  const a = tryParse(json);
  if (!a) return null;
  return (
    <div className="rounded-lg border p-3" style={panel(S)}>
      <div className="flex items-center gap-2 text-sm" style={{color:S.textSecondary}}>
        <MapPin size={16}/>{title}
      </div>
      <div className="mt-1 text-sm" style={{color:S.textPrimary}}>
        {a.adSoyad && <div className="font-medium">{a.adSoyad}</div>}
        <div>{[a.adresSatiri1, a.adresSatiri2].filter(Boolean).join(" ")}</div>
        <div>{[a.ilce, a.sehir, a.postaKodu].filter(Boolean).join(" / ")}</div>
        {a.ulke && <div>{a.ulke}</div>}
        {a.telefon && <div className="text-xs mt-1" style={{color:S.textSecondary}}>Tel: {a.telefon}</div>}
        {(a.vergiDairesi || a.vergiNo) && (
          <div className="text-xs mt-1" style={{color:S.textSecondary}}>
            VD: {a.vergiDairesi || "—"} • VN: {a.vergiNo || "—"}
          </div>
        )}
      </div>
    </div>
  );
};

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { customer } = useCustomerAuth();
  const { theme } = useTheme();
  const S = useSurfaces(theme);

  const [data, setData] = useState<any>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!customer?.id || !id) return;
      setLoading(true);
      const res = await orderApi.detail({ musteriId: customer.id, siparisId: Number(id) });
      setLoading(false);
      setData(res.success ? res.data : undefined);
    })();
  }, [customer?.id, id]);

  if (loading) {
    return (
      <div className="p-6 rounded-lg border" style={panel(S)}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-40 rounded" style={{ backgroundColor: S.surface2 }} />
          <div className="h-3 w-64 rounded" style={{ backgroundColor: S.surface2 }} />
          <div className="h-10 w-full rounded" style={{ backgroundColor: S.surface2 }} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 rounded-lg border" style={panel(S)}>
        <span style={{ color: S.textSecondary }}>Sipariş bulunamadı.</span>
      </div>
    );
  }

  const { header, items, payments, shipments } = data;
  const ccy = header?.paraBirimiKodu || "TRY";

  /* ===== Zaman çizelgesi: adımlar ===== */

  const steps = [
    { key: 0, label: "Alındı" },
    { key: 6, label: "Kargoda" },
    { key: 7, label: "Teslim" },
  ] as const;
  
  const isDanger =
    header.durumKodu === 4 || header.durumKodu === 18 ||
    header.durumKodu === 8 || header.durumKodu === 9;


  return (
    <div className="space-y-4">
      {/* Başlık */}
      <div className="rounded-lg border p-4" style={panel(S)}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="font-semibold" style={{ color: S.textPrimary }}>Sipariş #{header.siparisNo}</div>
            <div className="text-sm" style={{ color: S.textSecondary }}>
              Tarih: {new Date(header.siparisTarihi).toLocaleString("tr-TR")}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 text-sm px-2 py-1 rounded" style={{ backgroundColor: S.surface2, color: S.textPrimary }}>
              <Package2 size={16}/> {statusMap[header.durumKodu] || `Durum ${header.durumKodu}`}
            </span>
            <span className="inline-flex items-center gap-2 text-sm px-2 py-1 rounded" style={{ backgroundColor: S.surface2, color: S.textPrimary }}>
              <CreditCard size={16}/> {payMap[header.odemeDurumu] || `Ödeme ${header.odemeDurumu}`}
            </span>
          </div>
        </div>

        {/* Zaman çizelgesi (mobil uyumlu) */}
        <div className="mt-3 grid grid-cols-3 gap-3">
          {steps.map((s, i) => {
            const done = header.durumKodu >= s.key && !isDanger;
            const active = header.durumKodu === s.key && !isDanger;
            const danger = isDanger && (s.key !== 7); // başarısız akış
            return (
              <div key={s.key} className="flex items-center gap-2">
                <StepDot done={done} active={active} danger={danger}/>
                <div className="text-sm" style={{color: active ? S.accent : S.textSecondary}}>{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Adresler */}
      <div className="grid md:grid-cols-2 gap-3">
        <AddressBox title="Teslimat Adresi" json={header?.teslimatAdresiJson} S={S}/>
        <AddressBox title="Fatura Adresi" json={header?.faturaAdresiJson} S={S}/>
      </div>

      {/* Ödeme özeti + Kargo(lar) */}
      <div className="grid lg:grid-cols-2 gap-3">
        {/* Ödemeler */}
        <div className="rounded-lg border p-4" style={panel(S)}>
          <div className="flex items-center gap-2 mb-2" style={{color:S.textPrimary}}>
            <CreditCard size={18}/> <span className="font-medium">Ödemeler</span>
          </div>
          {(!payments || payments.length === 0) ? (
            <div className="text-sm" style={{color:S.textSecondary}}>Kayıtlı ödeme bulunamadı.</div>
          ) : (
            <div className="space-y-2">
              {payments.map((p: any) => (
                <div key={p.odemeIslemId} className="rounded border p-2 text-sm" style={{borderColor:S.divider}}>
                  <div className="flex justify-between">
                    <div className="font-medium" style={{color:S.textPrimary}}>{fmt(p.tutarBrut, p.paraBirimiKodu)}</div>
                    <div className="text-xs" style={{color:S.textSecondary}}>
                      {new Date(p.olusturmaTarihi).toLocaleString("tr-TR")}
                    </div>
                  </div>
                  <div className="text-xs mt-1" style={{color:S.textSecondary}}>
                    {p.odemeSaglayici || "—"} {p.islemAnahtari ? `• ${p.islemAnahtari}` : ""} • {p.islemDurumu===1?"Tahsil":p.islemDurumu===2?"İade":"Kısmi İade"}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Toplamlar */}
          <div className="mt-4 text-sm divide-y" style={{borderColor:S.divider}}>
            <div className="flex justify-between py-1">
              <span style={{color:S.textSecondary}}>Ara Toplam</span>
              <span style={{color:S.textPrimary}}>{fmt(header.araToplamNet, ccy)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span style={{color:S.textSecondary}}>İndirim</span>
              <span style={{color:S.textPrimary}}>{fmt(header.indirimToplamNet, ccy)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span style={{color:S.textSecondary}}>Kargo</span>
              <span style={{color:S.textPrimary}}>{fmt(header.kargoUcretiNet, ccy)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span style={{color:S.textSecondary}}>KDV</span>
              <span style={{color:S.textPrimary}}>{fmt(header.vergiToplam, ccy)}</span>
            </div>
            <div className="flex justify-between py-1 font-semibold">
              <span style={{color:S.textPrimary}}>Genel Toplam</span>
              <span style={{color:S.textPrimary}}>{fmt(header.genelToplamBrut, ccy)}</span>
            </div>
          </div>
        </div>

        {/* Sevkiyatlar */}
        <div className="rounded-lg border p-4" style={panel(S)}>
          <div className="flex items-center gap-2 mb-2" style={{color:S.textPrimary}}>
            <Truck size={18}/> <span className="font-medium">Kargo & Sevkiyat</span>
          </div>
          {(!shipments || shipments.length === 0) ? (
            <div className="text-sm" style={{color:S.textSecondary}}>Sevkiyat henüz oluşturulmamış.</div>
          ) : (
            <div className="space-y-2">
              {shipments.map((s: any) => (
                <div key={s.sevkiyatId} className="rounded border p-2 text-sm" style={{borderColor:S.divider}}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium" style={{color:S.textPrimary}}>
                      {s.kargoSirketAdi || "Kargo"} {s.takipNo ? `• ${s.takipNo}` : ""}
                    </div>
                    {!!s.takipNo && (
                      <a
                        href={`https://www.google.com/search?q=${encodeURIComponent(`${s.kargoSirketAdi || ""} ${s.takipNo}`)}`}
                        target="_blank" rel="noreferrer"
                        className="text-xs underline"
                        style={{color:S.accent}}
                        title="Takip et"
                      >
                        Takip et
                      </a>
                    )}
                  </div>
                  <div className="text-xs mt-1" style={{color:S.textSecondary}}>
                    Durum: {s.durumKodu===7?"Teslim":s.durumKodu===4?"Teslim Edilemedi":s.durumKodu===3?"Dağıtımda":s.durumKodu===2?"Yolda":s.durumKodu===1?"Hazırlanıyor":"Oluşturuldu"}
                    {" • "}
                    {new Date(s.olusturmaTarihi).toLocaleString("tr-TR")}
                  </div>
                  {s.notlar && <div className="text-xs mt-1" style={{color:S.textSecondary}}>Not: {s.notlar}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ürünler */}
      <div className="rounded-lg border p-4" style={panel(S)}>
        <div className="flex items-center gap-2 mb-2" style={{color:S.textPrimary}}>
          <Package2 size={18}/> <span className="font-medium">Sipariş Kalemleri</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left" style={{color:S.textSecondary}}>
                <th className="py-2 pr-3">Ürün</th>
                <th className="py-2 pr-3">Varyant</th>
                <th className="py-2 pr-3">Adet</th>
                <th className="py-2 pr-3">Birim (Net)</th>
                <th className="py-2 pr-3">KDV</th>
              </tr>
            </thead>
            <tbody style={{borderColor:S.divider}}>
              {items.map((it: any) => (
                <tr key={it.siparisKalemId} className="border-t">
                  <td className="py-2 pr-3">
                    <div className="font-medium" style={{color:S.textPrimary}}>{it.urunAdi}</div>
                    <div className="text-xs" style={{color:S.textSecondary}}>#{it.urunId}</div>
                  </td>
                  <td className="py-2 pr-3" style={{color:S.textPrimary}}>{it.secenekOzeti || "-"}</td>
                  <td className="py-2 pr-3" style={{color:S.textPrimary}}>{it.adet}</td>
                  <td className="py-2 pr-3" style={{color:S.textPrimary}}>{fmt(it.birimFiyatNet, ccy)}</td>
                  <td className="py-2 pr-3" style={{color:S.textPrimary}}>%{it.vergiOraniYuzde}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bilgilendirme notu (isteğe bağlı) */}
      <div className="text-xs" style={{color:S.textSecondary}}>
        Sorularınız için bizimle iletişime geçebilirsiniz. Kargo takip numarası varsa “Takip et” bağlantısından anlık durumu kontrol edebilirsiniz.
      </div>
    </div>
  );
};

export default OrderDetailPage;
